import { GridColDef } from "@mui/x-data-grid-pro";
import {
  GKButton,
  GKDataGrid,
  Grid2,
  PlusIcon,
  PopConfirm,
  Show,
  Typography,
} from "components";
import useGenerateQueryParams from "hooks/useGenerateQueryParams";
import { useFetchQuery, useMutation } from "hooks/useQueries";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "store/store";
import { handleRemoveUnderscore } from "utils";
import { moneyCommaSeparator } from "utils/MathFunction";
import {
  GET_ALL_INCOME_EXPENSE_BY_PORTFOLIO_ID,
  GET_ALL_INCOME_EXPENSE_LIST,
} from "utils/apiRoutes";
import axiosInstance from "utils/axiosInstance";
import { handleCapitalize, UserRoleTypes } from "utils/common";
import { returnParticularColor } from "utils/constants";
import { extraColumnsField, matchDate, refetchQuery } from "utils/helpers";
import { QueryKeys } from "utils/queryKey";
import useDocumentTitle from "utils/useDocumentTitle";

interface Props {
  id?: number | string;
  name?: string;
  tableName?: string;
  disableBtn?: boolean;
}

const columns: GridColDef[] = [
  {
    ...extraColumnsField("Date", "date", 150, 1),
    valueFormatter: (value) => matchDate(value.value),
  },
  {
    ...extraColumnsField("Portfolio", "clientName", 250, 1),
    renderCell: (params) => {
      return (
        <Grid2
          container
          display={"flex"}
          flexDirection={"column"}
          alignItems={"flex-start"}
        >
          <Typography
            fontWeight={700}
            maxWidth={"100%"}
            textOverflow={"ellipsis"}
            overflow={"hidden"}
          >
            {params?.row?.clientName}
          </Typography>
          <Typography fontSize={12}>
            {params?.row?.portfolioName} - {params?.row?.portfolioCode}
          </Typography>
        </Grid2>
      );
    },
  },
  {
    ...extraColumnsField("Transaction Type", "transactionType", 150, 1),
    valueFormatter: (params) => handleCapitalize(params?.value),
    renderCell: (params) => {
      return (
        <Typography
          fontWeight={600}
          style={{
            width: "fit-content",
            background: `${returnParticularColor(
              params?.row[`${params?.row?.transactionType?.toLowerCase()}`]
                ?.addedField || params?.row?.transactionType,
            )}33`,
            color: returnParticularColor(
              params?.row[`${params?.row?.transactionType?.toLowerCase()}`]
                ?.addedField || params?.row?.transactionType,
            ),
            padding: "4px 8px",
            borderRadius: 6,
          }}
          textTransform={"capitalize"}
        >
          {params?.row?.transactionType?.toLowerCase() === "sell" ||
          params?.row?.transactionType?.toLowerCase() === "buy"
            ? `BILLAMOUNT - ${params?.row?.transactionType}`
            : params?.row?.transactionType}
          &nbsp;
          <span
            style={{
              textTransform: "capitalize",
            }}
          >
            {params?.row[`${params?.row?.transactionType?.toLowerCase()}`]
              ? `/ ${handleRemoveUnderscore(
                  params?.row[`${params?.row?.transactionType?.toLowerCase()}`]
                    ?.addedField,
                )}`
              : ""}
          </span>
        </Typography>
      );
    },
  },
  {
    ...extraColumnsField("Bill Amount", "billAmount", 150, 1),
    type: "number",
    valueGetter: (params) => params.row.billAmount || "0",
    valueFormatter: (params) => moneyCommaSeparator(params.value),
  },
  {
    ...extraColumnsField("TDS", "tax", 150, 1),
    type: "number",
    valueGetter: (params) => params.row.tax || "0",
    valueFormatter: (params) => moneyCommaSeparator(params.value),
  },
  {
    ...extraColumnsField("Remarks", "remarks", 150, 1),
    valueGetter: (params) => params.row.remarks || "-",
  },
];

const IncomeExpense = (props: Props) => {
  useDocumentTitle(props?.id ? "Portfolio Reports" : "Income/Expense");
  const navigate = useNavigate();

  const { id, name, tableName, disableBtn = false } = props;

  const userData = useAppSelector((state) => state?.user?.userData);

  const [selectedIds, setSelectedIds] = useState<any>([]);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const requestBody = useGenerateQueryParams(
    name ? "portfolioIncomeExpense" : "incomeExpense",
  );

  const { data, isFetching } = useFetchQuery({
    route: id
      ? GET_ALL_INCOME_EXPENSE_BY_PORTFOLIO_ID(id)
      : GET_ALL_INCOME_EXPENSE_LIST,
    key: [
      id
        ? QueryKeys.PORTFOLIO_INCOME_EXPENSE_LIST
        : QueryKeys.INCOME_EXPENSE_LIST,
      requestBody,
    ],
    requestBody,
  });

  const { mutate: bulkDeleteIncomeExpense, isLoading } = useMutation({
    mutationFn: (ids: any) =>
      axiosInstance.delete(`/income_expense/bulk_delete/`, {
        data: { ids: ids },
      }),
    onSuccess: () => {
      refetchQuery(
        id
          ? QueryKeys.PORTFOLIO_INCOME_EXPENSE_LIST
          : QueryKeys.INCOME_EXPENSE_LIST,
      );
      setSelectedIds([]);
      setAnchorEl(null);
    },
  });

  return (
    <>
      <Grid2 size={12}>
        <GKDataGrid
          checkboxSelection={userData?.userRole !== UserRoleTypes.client}
          onRowSelectionModelChange={(params) => {
            setSelectedIds(params);
          }}
          rowSelectionModel={selectedIds}
          loading={isFetching}
          columns={columns}
          rows={data?.data?.data || []}
          name={name ? "portfolioIncomeExpense" : "incomeExpense"}
          maxHeight={"calc(100vh - 320px)"}
          onRowClick={(params) => {
            navigate(`/app/income-expense/${params?.row?.id}`);
          }}
          headerComponent={
            userData?.userRole !== UserRoleTypes.client && (
              <Grid2 container gap={1} justifyContent={"end"}>
                <Show.When isTrue={selectedIds?.length !== 0}>
                  <GKButton
                    variant="outlined"
                    loading={isLoading}
                    size="medium"
                    color="error"
                    onClick={(e) => {
                      setAnchorEl(e.currentTarget);
                    }}
                  >
                    Bulk Delete
                  </GKButton>
                </Show.When>
                <Show.When isTrue={selectedIds?.length !== 0}>
                  <GKButton
                    variant="outlined"
                    color="dark"
                    size="medium"
                    onClick={() => {
                      setSelectedIds([]);
                    }}
                  >
                    Cancel
                  </GKButton>
                </Show.When>
                <Show.When isTrue={selectedIds?.length === 0}>
                  <GKButton
                    variant="contained"
                    size="medium"
                    // onClick={() => setOpenIncomeExpenseModal(true)}
                    onClick={() => {
                      if (id) {
                        navigate(`/app/income-expense/portfolio/${id}`);
                      } else {
                        navigate("/app/income-expense");
                      }
                    }}
                    startIcon={<PlusIcon width={14} />}
                    disabled={disableBtn}
                  >
                    Add Income/Expense
                  </GKButton>
                </Show.When>
              </Grid2>
            )
          }
          tableName={tableName || "income_expense_list"}
          exportFile={{
            path: id
              ? GET_ALL_INCOME_EXPENSE_BY_PORTFOLIO_ID(id)
              : GET_ALL_INCOME_EXPENSE_LIST,
            exportName: "ExportIncomeExpense",
          }}
          totalCount={data?.data?.totalCount || 0}
        />
      </Grid2>

      <Show.When isTrue={Boolean(anchorEl)}>
        <PopConfirm
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          setOpen={setAnchorEl}
          onSuccess={() => bulkDeleteIncomeExpense(selectedIds)}
          message={`Are you sure that you want to delete the selected ledger statement & once deleted it cannot be reverted?`}
        />
      </Show.When>
    </>
  );
};

export default IncomeExpense;
